import ReceiverPicker from './ReceiverPicker';   // [RCV_PICKER_V1] 카테고리 → 그룹 → 수신자
import SenderSelect from './SenderSelect';   // [SMS_SENDER_PICK_V1] 발신번호 드롭다운
import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// SMS 전송 (설정탭)
//  - 수신처 명부에서 그룹/개인 단위로 선택해 발송
//  - 이미지 첨부 시 MMS(최대 3장), 없으면 LMS
//  - 이미지는 브라우저에서 자동 축소해 가비아 제한(300KB)을 맞춘다
// ─────────────────────────────────────────────

const formatPhone = (v = '') => {
  const n = String(v).replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0,3)}-${n.slice(3,7)}-${n.slice(7)}`;
  if (n.length === 10) return `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6)}`;
  return v;
};

// 한글 2바이트 기준 길이 (가비아 LMS 2000바이트 제한)
const byteLen = (s = '') =>
  Array.from(s).reduce((a, c) => a + (c.charCodeAt(0) > 127 ? 2 : 1), 0);

// ─── 이미지 자동 축소 ───
//  캔버스로 리사이즈 후 품질을 낮춰가며 목표 용량 이하로 만든다
//  (가비아 제한: 300KB, 이통사별 픽셀 제한 중 가장 엄격한 1500x1440 기준)
const shrinkImage = (file, maxKB = 280) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1440, MAX_H = 1440;
        let { width: w, height: h } = img;
        if (w > MAX_W || h > MAX_H) {
          const ratio = Math.min(MAX_W / w, MAX_H / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);

        // 품질을 단계적으로 낮춰 목표 용량을 맞춘다
        const tryQuality = (q) => {
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('이미지 변환 실패'));
            if (blob.size <= maxKB * 1024 || q <= 0.4) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
            } else {
              tryQuality(q - 0.1);
            }
          }, 'image/jpeg', q);
        };
        tryQuality(0.85);
      };
      img.onerror = () => reject(new Error('이미지를 읽지 못했습니다'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다'));
    reader.readAsDataURL(file);
  });

export default function SmsDirectSend() {
  const [receivers, setReceivers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);      // File[]
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [category, setCategory] = useState('전체');   // 구분 필터 (직원/거래처/고객)

  useEffect(() => { loadReceivers(); }, []);

  const loadReceivers = async () => {
    try {
      const res = await axios.get('/api/sms-receivers');
      // API 응답 형식: { ok: true, items: [...] }
      setReceivers(res.data?.items || (Array.isArray(res.data) ? res.data : []));
    } catch (e) {
      setMsg('수신처를 불러오지 못했습니다');
    }
  };

  // 명부에 등록된 구분 목록 (직원 / 거래처 / 고객 등)
  const categories = ['전체', ...new Set(receivers.map((r) => r.category).filter(Boolean))];

  // 선택한 구분으로 먼저 걸러낸 뒤 그룹별로 묶는다
  const filtered = category === '전체'
    ? receivers
    : receivers.filter((r) => r.category === category);

  const grouped = filtered.reduce((acc, r) => {
    const g = r.group_name || '미분류';
    (acc[g] = acc[g] || []).push(r);
    return acc;
  }, {});

  const toggleGroup = (group, checked) => {
    const ids = grouped[group].map((r) => r.id);
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))
    );
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  // ─── 이미지 선택 시 자동 축소 ───
  const pickImages = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    if (files.length === 0) return;
    setMsg('이미지 처리 중...');
    try {
      const shrunk = [];
      for (const f of files) {
        shrunk.push(await shrinkImage(f));
      }
      setImages(shrunk);
      const total = shrunk.reduce((a, f) => a + f.size, 0);
      setMsg(`이미지 ${shrunk.length}장 준비 완료 (총 ${Math.round(total / 1024)}KB)`);
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('❌ ' + err.message);
    }
  };

  // [SMS_SENDER_PICK_V1] 고른 발신번호 — 처음엔 기본번호로 채워지고, 저장하지 않음(이번 발송만)
  const [sender, setSender] = useState('');

  const send = async () => {
    if (selectedIds.length === 0) { setMsg('수신자를 선택하세요'); return; }
    if (!message.trim()) { setMsg('내용을 입력하세요'); return; }

    const targets = receivers.filter((r) => selectedIds.includes(r.id));
    const type = smsType;
    if (!window.confirm(`${targets.length}명에게 ${type}를 발송합니다.\n계속할까요?`)) return;

    setSending(true);
    setMsg('발송 중...');
    try {
      const fd = new FormData();
      fd.append('phones', targets.map((r) => r.phone).join(','));
      fd.append('message', message);
      if (sender) fd.append('sender', sender);   // [SMS_SENDER_PICK_V1] 비면 서버가 기본번호
      images.forEach((f) => fd.append('images', f));

      const res = await axios.post('/api/sms-direct/send', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setMsg(res.data.success ? `✅ ${res.data.message}` : `❌ ${res.data.message}`);
      // [SMS_DIRECT_RESET_V1] 발송 성공 시에만 내용·첨부 이미지를 비움
      //  실패하면 그대로 둬서 고쳐 다시 보낼 수 있게 / 수신자 선택(selectedIds)은 유지
      if (res.data.success) { setMessage(''); setImages([]); }
    } catch (e) {
      setMsg('❌ 발송 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setSending(false);
    }
  };

  const bytes = byteLen(message);

  // 발송 유형과 차감 건수 — 가비아 기준
  //  SMS 90바이트 이하 1건 / LMS 3건 / MMS(이미지) 10건
  const smsType = images.length > 0 ? 'MMS' : bytes <= 90 ? 'SMS' : 'LMS';
  const unitCost = images.length > 0 ? 10 : bytes <= 90 ? 1 : 3;
  const overLimit = bytes > 2000;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#1a202c' }}>
          📤 SMS 전송
        </h3>
        <a
          href="https://sms.gabia.com/send-result/"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'underline' }}
        >
          가비아에서 발송결과 확인 ↗
        </a>
      </div>

      {/* ─── 수신자 선택 ─── [RCV_PICKER_V1] 카테고리 → 그룹 → 수신자 (공용 ReceiverPicker) */}
      <div style={{ marginBottom: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', backgroundColor: '#f8fafc' }}>
        <label style={{ fontSize: '13px', fontWeight: 700 }}>수신자 선택</label>
        <ReceiverPicker receivers={receivers} selectedIds={selectedIds} onChange={setSelectedIds} maxHeight="220px" />
      </div>

      {/* ─── 내용 ─── */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '13px', fontWeight: 700 }}>내용</label>
          <span style={{ fontSize: '11px', color: overLimit ? '#dc2626' : '#94a3b8' }}>
            {bytes} / 2000 바이트
            <span style={{
              marginLeft: '6px', padding: '1px 6px', borderRadius: '8px',
              backgroundColor: smsType === 'SMS' ? '#dcfce7' : smsType === 'LMS' ? '#fef3c7' : '#dbeafe',
              color: smsType === 'SMS' ? '#166534' : smsType === 'LMS' ? '#92400e' : '#1e40af',
              fontWeight: 'bold',
            }}>
              {smsType} · {unitCost}건
            </span>
          </span>
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="보낼 내용을 입력하세요"
          style={{
            width: '100%', minHeight: '160px', padding: '10px',
            border: `1px solid ${overLimit ? '#dc2626' : '#e2e8f0'}`,
            borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ─── 이미지 첨부 ─── */}
      <div style={{ marginBottom: '15px', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
        <label style={{ fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
          이미지 첨부 (선택, 최대 3장)
        </label>
        <input type="file" accept="image/*" multiple onChange={pickImages} />
        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>
          큰 사진은 자동으로 줄여서 보냅니다. 이미지를 넣으면 MMS로 발송됩니다.
        </div>

        {images.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {images.map((f, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <img
                  src={URL.createObjectURL(f)}
                  alt={`첨부 ${i + 1}`}
                  style={{ width: '80px', height: '80px', objectFit: 'cover', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                  {Math.round(f.size / 1024)}KB
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setImages([])}
              style={{ alignSelf: 'flex-start', fontSize: '11px', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '3px', cursor: 'pointer', padding: '4px 8px' }}
            >
              첨부 삭제
            </button>
          </div>
        )}
      </div>


      {/* ─── 미리보기 ─── */}
      {(message.trim() || images.length > 0) && (
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
            📱 미리보기
          </div>
          <div style={{
            border: '1px solid #cbd5e0', borderRadius: '12px',
            backgroundColor: '#fff', overflow: 'hidden',
            maxWidth: '320px', margin: '0 auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            {/* 말풍선 */}
            <div style={{ padding: '12px' }}>
              <div style={{
                backgroundColor: '#fef3c7', borderRadius: '10px',
                padding: '10px 12px', fontSize: '13px',
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                lineHeight: 1.5,
              }}>
                {message || <span style={{ color: '#a0aec0' }}>내용을 입력하세요</span>}
              </div>

              {/* 첨부 이미지 */}
              {images.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {images.map((f, i) => (
                    <img
                      key={i}
                      src={URL.createObjectURL(f)}
                      alt={`첨부 ${i + 1}`}
                      style={{
                        width: '90px', height: '90px', objectFit: 'cover',
                        borderRadius: '8px', border: '1px solid #e2e8f0',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 발송 요약 — 실제로 몇 건이 차감되는지 미리 알려준다 */}
          <div style={{
            textAlign: 'center', fontSize: '11px', color: '#64748b',
            marginTop: '8px',
          }}>
            수신 {selectedIds.length}명 · {smsType}
            {' · '}건당 {unitCost}건 차감
            {selectedIds.length > 0 && ` · 총 ${selectedIds.length * unitCost}건`}
          </div>
        </div>
      )}
      {/* [SMS_SENDER_PICK_V1] 발신번호 선택 — 두 가비아 계정의 등록 번호 */}
      <SenderSelect kind="direct" value={sender} onChange={setSender} style={{ margin: '8px 0' }} />

      {/* ─── 발송 ─── */}
      <button
        onClick={send}
        disabled={sending || overLimit}
        style={{
          width: '100%', padding: '12px',
          backgroundColor: sending || overLimit ? '#ccc' : '#2563eb',
          color: 'white', border: 'none', borderRadius: '4px',
          cursor: sending || overLimit ? 'not-allowed' : 'pointer',
          fontSize: '15px', fontWeight: 'bold',
        }}
      >
        {sending ? '발송 중...' : `📤 ${smsType} 발송`}
      </button>

      {msg && (
        <div style={{
          marginTop: '12px', padding: '10px', borderRadius: '4px',
          backgroundColor: '#f1f5f9', fontSize: '13px', textAlign: 'center',
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}
